import { Link, useParams } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import Navbar from '../../components/Navbar';
import Chatbot from '../../components/Chatbot';

export default function QueriesInLoopsPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to={`${basePath}/help`} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            ← Back to Help Center
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">Queries in Loops</h1>
          <p className="text-lg text-gray-600 mt-2">
            Understanding and fixing one of the most critical Flow performance issues
          </p>
        </div>

        {/* Critical Alert */}
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
          <div className="flex">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-400 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Critical Performance Issue</h3>
              <p className="mt-2 text-sm text-red-700">
                Queries inside loops are the #1 cause of Salesforce governor limit errors in Flows.
                A single flow execution can quickly exceed the 100 SOQL query limit.
              </p>
            </div>
          </div>
        </div>

        {/* What is it? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What Are Queries in Loops?</h2>
          <p className="text-gray-700 mb-4">
            A "Query in Loop" occurs when you place a Get Records element inside a Loop element.
            Each iteration of the loop executes the query, which means:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Processing 10 records = 10 queries</li>
            <li>Processing 50 records = 50 queries</li>
            <li>Processing 200 records = 200 queries (exceeds 100 query limit)</li>
          </ul>
        </div>

        {/* Why is it bad? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Is This a Problem?</h2>

          <div className="space-y-4">
            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="font-semibold text-gray-900">Governor Limit Violations</h3>
              <p className="text-gray-700 mt-1">
                Salesforce allows only 100 SOQL queries per transaction. With queries in loops,
                you can easily hit this limit with just 100 trigger records.
              </p>
            </div>

            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="font-semibold text-gray-900">Slow Performance</h3>
              <p className="text-gray-700 mt-1">
                Each query takes time. 200 queries can take several seconds to complete,
                leading to timeout errors and poor user experience.
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="font-semibold text-gray-900">System Instability</h3>
              <p className="text-gray-700 mt-1">
                When flows fail due to governor limits, your automation breaks and data
                may not be processed correctly.
              </p>
            </div>
          </div>
        </div>

        {/* How to identify */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Identify This Issue</h2>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">In FlowDocs:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Check your Performance Scorecard on the flow detail page</li>
                <li>Look for "Queries in Loops" in the Issues Detected section</li>
                <li>Review the Recommended Fixes tab for specific elements</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">In Salesforce Flow Builder:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Look for Get Records elements connected after a Loop element</li>
                <li>Check if the Get Records uses a field from the loop's current item</li>
                <li>Example: Loop through Accounts, then Get Records WHERE AccountId = CurrentAccount.Id</li>
              </ul>
            </div>
          </div>
        </div>

        {/* The Fix */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Fix It</h2>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
            <div className="flex">
              <LightBulbIcon className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-green-800">The Solution</h3>
                <p className="mt-2 text-sm text-green-700">
                  Query once BEFORE the loop to get all needed records, then use the collection inside the loop.
                </p>
              </div>
            </div>
          </div>

          <h3 className="font-semibold text-gray-900 mb-3">Step-by-Step Fix:</h3>

          <ol className="space-y-6">
            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  1
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Identify What You're Querying For</h4>
                  <p className="text-gray-700 mt-1">
                    Look at the Get Records inside your loop. What field from the loop variable are you using in the filter?
                  </p>
                  <div className="bg-gray-100 p-3 rounded mt-2 font-mono text-sm">
                    Example: Get Records WHERE AccountId = &#123;!CurrentItem.AccountId&#125;
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  2
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Collect All IDs Before the Loop</h4>
                  <p className="text-gray-700 mt-1">
                    Create an Assignment element before the loop that adds all relevant IDs to a collection variable.
                  </p>
                  <div className="bg-gray-100 p-3 rounded mt-2 font-mono text-sm">
                    collectionOfAccountIds = &#123;!loopCollection.AccountId&#125;
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  3
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Query Once Using IN Clause</h4>
                  <p className="text-gray-700 mt-1">
                    Before the loop, add a Get Records that retrieves ALL needed records at once.
                  </p>
                  <div className="bg-gray-100 p-3 rounded mt-2 font-mono text-sm">
                    Get Records WHERE Id IN &#123;!collectionOfAccountIds&#125;
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  4
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Use the Pre-Queried Collection</h4>
                  <p className="text-gray-700 mt-1">
                    Inside the loop, reference the collection you queried earlier. Use a nested loop or filter the collection as needed.
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  5
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Remove the Query from the Loop</h4>
                  <p className="text-gray-700 mt-1">
                    Delete the Get Records element that was inside the loop.
                  </p>
                </div>
              </div>
            </li>
          </ol>
        </div>

        {/* Before/After Example */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Before & After Example</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Before */}
            <div className="border-2 border-red-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <XCircleIcon className="h-6 w-6 text-red-500 mr-2" />
                <h3 className="font-semibold text-red-700">Before (Bad)</h3>
              </div>
              <div className="space-y-2 text-sm font-mono bg-gray-50 p-3 rounded">
                <div className="text-gray-600">// Loop through Accounts</div>
                <div className="pl-4">Loop: AccountCollection</div>
                <div className="pl-8 text-red-600">Get Records: Contacts</div>
                <div className="pl-8 text-red-600">WHERE AccountId = CurrentAccount.Id</div>
                <div className="pl-8">Update Records: Contacts</div>
              </div>
              <div className="mt-3 text-sm text-red-700">
                Result: 200 accounts = 200 queries = FAILURE
              </div>
            </div>

            {/* After */}
            <div className="border-2 border-green-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="font-semibold text-green-700">After (Good)</h3>
              </div>
              <div className="space-y-2 text-sm font-mono bg-gray-50 p-3 rounded">
                <div className="text-green-600">Get Records: All Contacts</div>
                <div className="text-green-600">WHERE AccountId IN AccountCollection.Id</div>
                <div className="text-gray-600 mt-2">// Loop through Accounts</div>
                <div className="pl-4">Loop: AccountCollection</div>
                <div className="pl-8">// Use pre-queried ContactCollection</div>
                <div className="pl-8">Update Records: Matching Contacts</div>
              </div>
              <div className="mt-3 text-sm text-green-700">
                Result: 200 accounts = 1 query = SUCCESS
              </div>
            </div>
          </div>
        </div>

        {/* Impact */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
          <h2 className="text-xl font-bold text-blue-900 mb-2">Expected Improvement</h2>
          <div className="text-blue-800 space-y-2">
            <p className="font-semibold">SOQL Query Usage: 99% reduction</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Before: Up to 200 queries (exceeds limit)</li>
              <li>After: 1-2 queries (well within limit)</li>
            </ul>
            <p className="mt-4">
              This fix transforms a flow that fails with moderate data volumes into one that can handle
              200+ records without issue.
            </p>
          </div>
        </div>

        {/* Related Resources */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Resources</h2>
          <ul className="space-y-3">
            <li>
              <Link to={`${basePath}/help/dml-in-loops`} className="text-blue-600 hover:text-blue-800 font-medium">
                DML Operations in Loops →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Similar pattern with database updates</p>
            </li>
            <li>
              <Link to={`${basePath}/help/bulkification`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Bulkification →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Processing multiple records efficiently</p>
            </li>
            <li>
              <Link to={`${basePath}/help/best-practices`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Best Practices →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Complete guide to high-performance flows</p>
            </li>
          </ul>
        </div>
      </div>

      <Chatbot />
    </div>
  );
}
